import { sanitizeProjectedRecord } from "./commercialTracking.mjs";

export function getClosingDate(client) {
  return client.data_fechamento
    || client.stageDates?.closed
    || client.stageDates?.posVenda
    || client.stageDates?.nextSale
    || client.createdAt
    || "";
}

export function wasSold(client) {
  if (client.manualEntrega) return false;
  if (["closed", "posVenda"].includes(client.stage)) return true;
  return client.stage === "nextSale"
    && Boolean(client.stageDates?.closed || client.stageDates?.posVenda);
}

export function isHistoricalLiftPostSaleCard(client) {
  if (client?.liftHistoricalPostSaleOnly !== true
    || Number(client?.liftStoreId) !== Number(client?.storeId)
    || client?.liftPedidoId === null || client?.liftPedidoId === undefined
    || String(client.liftPedidoId) === "") return false;
  const storeId = Number(client.storeId);
  const pedidoId = String(client.liftPedidoId);
  return Object.values(client.liftOrders || {}).some(link =>
    link && Number(link.storeId) === storeId
      && String(link.liftPedidoId || "") === pedidoId
      && String(link.path || "") === `arflow_lift_sales/${storeId}/${pedidoId}`);
}

// Dashboard and consultant ranking must use the persisted client sale ledger as
// their revenue authority. Lead/prospect source collections are attribution
// indexes and can contain the same sale more than once after conversion.
export function computeCanonicalDashboardSales(options = {}) {
  return computeSalesLedger({
    ...options,
    // Operational tracking may project an open conversation stage over a
    // persisted closed sale. Revenue must use the saved commercial fact.
    clients:(options.clients || []).map(client => sanitizeProjectedRecord(client)),
    leads: [],
    prospeccao: [],
    // Source collections are attribution indexes for LIFT's explicit CRM link,
    // not additional dashboard revenue authorities.
    attributionLeads: options.leads || [],
    attributionProspeccao: options.prospeccao || [],
  });
}

const sameId = (left, right) => Number(left) === Number(right);
const sameRecordId = (left, right) => left !== null
  && left !== undefined
  && right !== null
  && right !== undefined
  && String(left) === String(right);

function computeLegacySalesLedger({
  clients = [],
  leads = [],
  prospeccao = [],
  periodRange,
  storeId,
  consultantId,
  inPeriod,
  normalizePhone,
  users = [],
}) {
  const hasStoreScope = storeId !== null && storeId !== undefined && storeId !== "";
  const hasConsultantScope = consultantId !== null && consultantId !== undefined && consultantId !== "";
  const phoneKey = (value) => normalizePhone(value || "");
  const storeForLead = (lead) => lead.storeId
    || users.find((user) => sameId(user.id, lead.consultantId))?.storeId
    || 1;
  const storeForProspect = (prospect) => prospect.storeId
    || users.find((user) => sameId(user.id, prospect.consultantId))?.storeId
    || 1;
  const hasSamePhone = (sourcePhone, clientPhone) => {
    const sourceKey = phoneKey(sourcePhone);
    const clientKey = phoneKey(clientPhone);
    return Boolean(sourceKey && clientKey && sourceKey === clientKey);
  };
  const hasAnySourceLink = (client) => Boolean(
    client.financialSourceType
    || client.financialSourceId
    || client.leadId
    || client.lead_id
    || client.prospId
    || client.prosp_id
    || client.prospeccaoId
    || client.prospeccao_id
  );
  const hasExplicitSourceLink = (source, client, sourceType) => (
    sameRecordId(source.clientId || source.client_id || source.clienteId || source.cliente_id, client.id)
    || (sourceType === "lead" && (
      (client.financialSourceType === "lead" && sameRecordId(client.financialSourceId, source.id))
      || sameRecordId(client.leadId || client.lead_id, source.id)
    ))
    || (sourceType === "prospeccao" && (
      (client.financialSourceType === "prospeccao" && sameRecordId(client.financialSourceId, source.id))
      || sameRecordId(client.prospId || client.prosp_id || client.prospeccaoId || client.prospeccao_id, source.id)
    ))
  );
  const matchesSourceClient = (source, client, sourceType, sourceStoreId, sourcePhone) => {
    if (!sameId(sourceStoreId, client.storeId || 1)) return false;
    if (hasExplicitSourceLink(source, client, sourceType)) return true;
    const sourceId = String(source.id || "").replace(/^aurora_/, "");
    const isManualLeadSale = sourceType === "lead"
      && (source.manualFechamento || sourceId.startsWith("manual_"));
    // Older records have no immutable source ID. Phone alone is not enough to
    // collapse a newly registered manual sale: it is always its own sale source.
    return !isManualLeadSale && !hasAnySourceLink(client) && hasSamePhone(sourcePhone, client.phone);
  };
  const leadMatchesClient = (lead, client) => matchesSourceClient(
    lead,
    client,
    "lead",
    storeForLead(lead),
    lead.telefone || lead.phone
  );
  const prospectMatchesClient = (prospect, client) => matchesSourceClient(
    prospect,
    client,
    "prospeccao",
    storeForProspect(prospect),
    prospect.phone || prospect.telefone
  );
  const inStore = (record, derivedStoreId = record.storeId || 1) => (
    !hasStoreScope || sameId(derivedStoreId, storeId)
  );
  const belongsToConsultant = (record) => (
    !hasConsultantScope || sameId(record.consultantId, consultantId)
  );
  // Only cards explicitly created as historical LIFT post-sale projections,
  // with an exact matching immutable source link, are excluded from revenue.
  // Older clients and ordinary closed clients remain financial facts.
  const financialClients = clients.filter(client => !isHistoricalLiftPostSaleCard(client));
  const storeClients = financialClients.filter((client) => inStore(client));
  const scopeClients = storeClients.filter(belongsToConsultant);
  const storeLeads = leads.filter((lead) => inStore(lead, storeForLead(lead)));
  const scopeLeads = storeLeads.filter(belongsToConsultant);
  const storeProsp = prospeccao.filter((prospect) => inStore(prospect, storeForProspect(prospect)));
  const scopeProsp = storeProsp.filter(belongsToConsultant);

  const leadSaleDate = (lead) => lead.fechou_venda_em || lead.data_fechamento || lead.createdAt;
  const leadClosedKanban = storeLeads.filter((lead) => (
    !lead.manualFechamento
    && lead.estagio === "fechou_venda"
  ));
  const leadClosedManual = storeLeads.filter((lead) => (
    lead.manualFechamento
    && lead.estagio === "fechou_venda"
  ));
  const leadSources = [...leadClosedKanban, ...leadClosedManual];
  const leadClosedClients = scopeClients.filter((client) => (
    client.origin === "Lead"
    && wasSold(client)
    && inPeriod(getClosingDate(client), periodRange)
    && !leadSources.some((lead) => leadMatchesClient(lead, client))
  ));
  const leadArrived = scopeLeads.filter((lead) => (
    !lead.manualFechamento && inPeriod(lead.data_criacao, periodRange)
  )).length;
  const toLeadSale = (lead) => {
    const leadPhone = phoneKey(lead.telefone || lead.phone);
    const clientMatch = storeClients.find((client) => (
      leadMatchesClient(lead, client)
      && wasSold(client)
    ));
    return {
      id: `lead:${lead.id || lead._rawPhone || leadPhone}`,
      source: lead.manualFechamento ? "Lead — fechamento manual" : "Lead",
      origin: "Lead",
      recordType: "lead",
      name: lead.nome || lead.name || clientMatch?.name || "Sem nome",
      phone: lead.telefone || lead.phone || clientMatch?.phone || "",
      consultantId: Number(clientMatch?.consultantId ?? lead.consultantId),
      storeId: Number(storeForLead(lead)),
      value: clientMatch ? (clientMatch.value || 0) : Number(lead.valor_venda || 0),
      saleDate: clientMatch ? getClosingDate(clientMatch) : leadSaleDate(lead),
      client: clientMatch || null,
      record: lead,
    };
  };
  const leadSales = [
    ...leadClosedClients.map((client) => ({
      id: `client:${client.id}`,
      source: "Atendimento Presencial",
      origin: "Lead",
      recordType: "client",
      name: client.name,
      phone: client.phone,
      consultantId: Number(client.consultantId),
      storeId: Number(client.storeId || 1),
      value: client.value || 0,
      saleDate: getClosingDate(client),
      client,
      record: client,
    })),
    ...leadSources.map(toLeadSale).filter((sale) => (
      inPeriod(sale.saleDate, periodRange)
      && (!hasConsultantScope || sameId(sale.consultantId, consultantId))
    )),
  ];

  const prospContatados = scopeProsp.filter((prospect) => inPeriod(prospect.date || prospect.createdAt, periodRange));
  const prospVendidosKanban = storeProsp.filter((prospect) => (
    prospect.stage === "venda"
  ));
  const prospSoldClients = scopeClients.filter((client) => (
    client.origin === "Prospecção"
    && wasSold(client)
    && inPeriod(getClosingDate(client), periodRange)
    && !prospVendidosKanban.some((prospect) => prospectMatchesClient(prospect, client))
  ));
  const toProspSale = (prospect) => {
    const clientMatch = storeClients.find((client) => (
      prospectMatchesClient(prospect, client)
      && wasSold(client)
    ));
    return {
      id: `prosp:${prospect.id}`,
      source: "Prospecção",
      origin: "Prospecção",
      recordType: "prospeccao",
      name: prospect.name || clientMatch?.name || "Sem nome",
      phone: prospect.phone || prospect.telefone || clientMatch?.phone || "",
      consultantId: Number(clientMatch?.consultantId ?? prospect.consultantId),
      storeId: Number(storeForProspect(prospect)),
      value: clientMatch ? (clientMatch.value || 0) : (prospect.value || 0),
      saleDate: clientMatch
        ? getClosingDate(clientMatch)
        : (prospect.saleDate || prospect.date || prospect.createdAt),
      client: clientMatch || null,
      record: prospect,
    };
  };
  const prospSales = [
    ...prospVendidosKanban.map(toProspSale).filter((sale) => (
      inPeriod(sale.saleDate, periodRange)
      && (!hasConsultantScope || sameId(sale.consultantId, consultantId))
    )),
    ...prospSoldClients.map((client) => ({
      id: `client:${client.id}`,
      source: "Atendimento Presencial",
      origin: "Prospecção",
      recordType: "client",
      name: client.name,
      phone: client.phone,
      consultantId: Number(client.consultantId),
      storeId: Number(client.storeId || 1),
      value: client.value || 0,
      saleDate: getClosingDate(client),
      client,
      record: client,
    })),
  ];

  const portaClients = scopeClients.filter((client) => (
    client.origin !== "Lead"
    && client.origin !== "Prospecção"
    && wasSold(client)
    && inPeriod(getClosingDate(client), periodRange)
  ));
  const portaSales = portaClients.map((client) => ({
    id: `client:${client.id}`,
    source: "Atendimento Presencial",
    origin: client.origin || "Atendimento Presencial",
    recordType: "client",
    name: client.name,
    phone: client.phone,
    consultantId: Number(client.consultantId),
    storeId: Number(client.storeId || 1),
    value: client.value || 0,
    saleDate: getClosingDate(client),
    client,
    record: client,
  }));

  // Atendimento/financeiro trabalha com todas as fichas de cliente fechadas.
  // Isso inclui um Lead que já foi convertido em cliente, sem duplicar o livro
  // comercial por origem acima.
  const atendimentoClients = scopeClients.filter((client) => (
    wasSold(client) && inPeriod(getClosingDate(client), periodRange)
  ));
  const atendimentoSales = atendimentoClients.map((client) => ({
    id: `client:${client.id}`,
    source: "Atendimento Presencial",
    origin: client.origin || "Atendimento Presencial",
    recordType: "client",
    name: client.name,
    phone: client.phone,
    consultantId: Number(client.consultantId),
    storeId: Number(client.storeId || 1),
    value: client.value || 0,
    saleDate: getClosingDate(client),
    client,
    record: client,
  }));
  const atendidos = scopeClients.filter((client) => inPeriod(client.createdAt, periodRange));

  const lead = {
    qtd: leadSales.length,
    value: leadSales.reduce((sum, sale) => sum + sale.value, 0),
    arrived: leadArrived,
    conv: 0,
  };
  lead.conv = lead.arrived ? Math.min(100, Math.round((lead.qtd / lead.arrived) * 100)) : 0;
  const prospeccaoResult = {
    qtd: prospSales.length,
    value: prospSales.reduce((sum, sale) => sum + sale.value, 0),
    arrived: prospContatados.length,
    conv: 0,
  };
  prospeccaoResult.conv = prospeccaoResult.arrived
    ? Math.min(100, Math.round((prospeccaoResult.qtd / prospeccaoResult.arrived) * 100))
    : 0;
  const porta = {
    qtd: portaSales.length,
    value: portaSales.reduce((sum, sale) => sum + sale.value, 0),
    arrived: scopeClients.filter((client) => (
      client.origin !== "Lead"
      && client.origin !== "Prospecção"
      && inPeriod(client.createdAt, periodRange)
    )).length,
    conv: 0,
  };
  porta.conv = porta.arrived ? Math.min(100, Math.round((porta.qtd / porta.arrived) * 100)) : 0;
  const atendimento = {
    qtd: atendimentoSales.length,
    value: atendimentoSales.reduce((sum, sale) => sum + sale.value, 0),
    arrived: atendidos.length,
    conv: 0,
  };
  atendimento.conv = atendimento.arrived
    ? Math.min(100, Math.round((atendimento.qtd / atendimento.arrived) * 100))
    : 0;

  const sales = [...leadSales, ...prospSales, ...portaSales];
  const totalQtd = sales.length;
  const totalValue = sales.reduce((sum, sale) => sum + sale.value, 0);
  return {
    lead,
    prospeccao: prospeccaoResult,
    porta,
    atendimento,
    totalQtd,
    totalValue,
    ticketMedio: totalQtd ? Math.round(totalValue / totalQtd) : 0,
    sales,
    leadClosedList: [...leadClosedClients, ...leadClosedKanban, ...leadClosedManual],
    prospVendidosList: [...prospVendidosKanban, ...prospSoldClients],
    portaVendidosList: portaClients,
    atendVendidosList: atendimentoClients,
  };
}

const LIFT_CUTOFF = "2026-10-01";
const UNKNOWN_ORIGIN = "Origem não identificada";
const LIFT_ORIGINS = {
  lead_anuncio: { origin:"Lead", category:"lead" },
  anuncio_loja: { origin:"Lead", category:"lead", visitDirect:true },
  porta: { origin:"Atendimento Presencial", category:"porta" },
  prospeccao: { origin:"Prospecção", category:"prospeccao" },
  indicacao: { origin:"Indicação", category:"indicacao" },
};

function saoPauloDay(value) {
  if (!value) return "";
  const text = String(value);
  const dateOnly = text.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (dateOnly) return dateOnly[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"America/Sao_Paulo", year:"numeric", month:"2-digit", day:"2-digit",
  }).formatToParts(parsed);
  const part = type => parts.find(item => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function periodBoundaryDay(value) {
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const pad = number => String(number).padStart(2, "0");
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  const text = String(value);
  const datePart = text.match(/^(\d{4}-\d{2}-\d{2})(?:$|T| )/);
  return datePart ? datePart[1] : saoPauloDay(value);
}

function liftSaleInPeriod(day, options) {
  const range = options.periodRange;
  const start = periodBoundaryDay(range?.start ?? range?.from);
  const end = periodBoundaryDay(range?.end ?? range?.to);
  if (start || end) return (!start || day >= start) && (!end || day <= end);
  return options.inPeriod ? Boolean(options.inPeriod(day, range)) : true;
}

function exactCrmKeys(record) {
  const keys = [record?.chaveCRM, record?.chave, record?.key, record?.id, record?._firebaseKey, record?._rawPhone]
    .filter(value => value !== null && value !== undefined && String(value) !== "")
    .map(String);
  if (String(record?.id || "").startsWith("aurora_")) keys.push(String(record.id).slice(7));
  return [...new Set(keys)];
}

function parseCrmLink(value) {
  if (value === null || value === undefined || String(value) === "") return null;
  const fullKey = String(value);
  const parts = fullKey.split("/").filter(Boolean);
  if (parts.length < 2) return { fullKey, key:fullKey, sourceType:null };
  const first = parts[0].toLowerCase();
  const sourceType = ["lead", "leads"].includes(first) ? "lead"
    : ["client", "clients", "arflow_clients"].includes(first) ? "client"
      : ["arflow_prospeccao", "prospeccao", "prospeccoes", "prospect", "prospects"].includes(first) ? "prospeccao"
        : null;
  return { fullKey, key:parts[parts.length - 1], sourceType };
}

function sameStore(left, right) {
  return left !== null && left !== undefined && right !== null && right !== undefined
    && String(left) === String(right);
}

function liftLinkMatches(linkKey, storeId, records) {
  const parsed = parseCrmLink(linkKey);
  if (!parsed) return [];
  return records.filter(item => {
    if (!sameStore(item.storeId, storeId)) return false;
    if (parsed.sourceType && item.sourceType !== parsed.sourceType) return false;
    const keys = exactCrmKeys(item.record);
    return keys.includes(parsed.fullKey) || keys.includes(parsed.key);
  });
}

function originFromLinkedRecord(linkedMatch) {
  if (!linkedMatch) return null;
  if (linkedMatch.sourceType === "lead") return { origin:"Lead", category:"lead" };
  if (linkedMatch.sourceType === "prospeccao") return { origin:"Prospecção", category:"prospeccao" };
  const value = linkedMatch.record?.origin || linkedMatch.record?.origem || "";
  if (value === "Lead") return { origin:"Lead", category:"lead" };
  if (value === "Prospecção") return { origin:"Prospecção", category:"prospeccao" };
  if (["Indicação", "Indicacao", "indicacao"].includes(value)) return { origin:"Indicação", category:"indicacao" };
  if (value && value !== "Atendimento Presencial") return { origin:value, category:"porta" };
  if (value === "Atendimento Presencial") return { origin:value, category:"porta" };
  return null;
}

function addLiftSales(legacy, options) {
  const liftSales = options.liftSales || [];
  const userStoreFor = record => options.users?.find(user => sameStore(user.id, record?.consultantId))?.storeId;
  const allSourceRecords = [
    ...(options.clients || []).map(record => ({ record, sourceType:"client", storeId:record.storeId || 1 })),
    ...(options.leads || []).map(record => ({ record, sourceType:"lead", storeId:record.storeId || userStoreFor(record) || 1 })),
    ...(options.prospeccao || []).map(record => ({ record, sourceType:"prospeccao", storeId:record.storeId || userStoreFor(record) || 1 })),
    ...(options.attributionLeads || []).map(record => ({ record, sourceType:"lead", storeId:record.storeId || userStoreFor(record) || 1 })),
    ...(options.attributionProspeccao || []).map(record => ({ record, sourceType:"prospeccao", storeId:record.storeId || userStoreFor(record) || 1 })),
  ];
  const liftRecords = [];
  const redactedLiftRecords = new Set();
  const seenLiftKeys = new Set();
  for (const record of liftSales) {
    // Historical imports support post-sale follow-up only, never commercial
    // revenue. Keep the marker authoritative even if the source sale date is
    // corrected into the October-and-later financial window.
    if (record?.historicoSomentePosVenda === true) continue;
    const day = saoPauloDay(record?.dataVenda);
    if (!day || day < LIFT_CUTOFF || record?.sumiuDoLiftEm
      || record?.liftCancellation?.status === "cancelled"
      || ["CANCELADO","CANCELADA","CANCELED","CANCELLED","INATIVO","ANULADO","ANULADA"]
        .includes(String(record?.statusLift || "").trim().toUpperCase())) continue;
    const store = record.storeId;
    const pedido = record.liftPedidoId;
    const hasPedidoId = pedido !== null && pedido !== undefined && String(pedido) !== "";
    const redactedProjection = !hasPedidoId
      && record.id !== null && record.id !== undefined && String(record.id) !== "";
    if (store === null || store === undefined || (!hasPedidoId && !redactedProjection)) continue;
    const uniqueKey = `${String(store)}\u0000${String(hasPedidoId ? pedido : record.id)}`;
    if (seenLiftKeys.has(uniqueKey)) continue;
    seenLiftKeys.add(uniqueKey);
    if (!liftSaleInPeriod(day, options)) continue;
    liftRecords.push(record);
    if (redactedProjection) redactedLiftRecords.add(record);
  }

  const hasStoreScope = options.storeId !== null && options.storeId !== undefined && options.storeId !== "";
  const hasConsultantScope = options.consultantId !== null && options.consultantId !== undefined && options.consultantId !== "";
  const consultantHomeStore = hasConsultantScope
    ? (hasStoreScope ? options.storeId : userStoreFor({ consultantId:options.consultantId }))
    : null;
  const manualWithoutLift = [];
  const postCutoffLegacy = legacy.sales.filter(sale => saoPauloDay(sale.saleDate) >= LIFT_CUTOFF);
  for (const sale of postCutoffLegacy) {
    const saleKeys = [...exactCrmKeys(sale.record), ...exactCrmKeys(sale.client)];
    const matchingLift = liftRecords.filter(lift => (
      sameStore(lift.storeId, sale.storeId)
      && String(lift.vinculo?.chaveCRM ?? "") !== ""
      && liftLinkMatches(lift.vinculo.chaveCRM, lift.storeId, allSourceRecords).length === 1
      && liftLinkMatches(lift.vinculo.chaveCRM, lift.storeId, allSourceRecords)
        .some(match => exactCrmKeys(match.record).some(key => saleKeys.includes(key)))
    ));
    if (matchingLift.length) continue;
    const ambiguous = saleKeys.some(key => liftRecords.some(lift => (
      sameStore(lift.storeId, sale.storeId)
      && liftLinkMatches(lift.vinculo?.chaveCRM, lift.storeId, allSourceRecords).length > 1
      && liftLinkMatches(lift.vinculo?.chaveCRM, lift.storeId, allSourceRecords)
        .some(match => exactCrmKeys(match.record).includes(key))
    )));
    manualWithoutLift.push({
      ...sale,
      warning: ambiguous
        ? "Vínculo ambíguo; confirme manualmente no LIFT."
        : "Venda manual de outubro sem correspondência confirmada no LIFT.",
      unmatched: true,
    });
  }

  const liftCommercialSales = [];
  let unknownOriginCount = 0;
  for (const lift of liftRecords) {
    const storeId = lift.storeId;
    const sellerId = lift.vendidoPor?.consultantId;
    const bindingState = lift.vinculo?.crmOriginBinding?.status;
    const classificationState = lift.classificacaoOrigem?.bindingStatus;
    const pendingAttribution = Boolean(
      (bindingState && bindingState !== "confirmed")
      || (classificationState && classificationState !== "confirmed")
    );
    const linkedMatches = pendingAttribution ? []
      : liftLinkMatches(lift.vinculo?.chaveCRM, storeId, allSourceRecords);
    const linkedMatch = linkedMatches.length === 1 ? linkedMatches[0] : null;
    const originInfo = pendingAttribution ? null : (
      LIFT_ORIGINS[lift.classificacaoOrigem?.tipo] || originFromLinkedRecord(linkedMatch)
    );
    const linkKey = pendingAttribution ? null : lift.vinculo?.chaveCRM;
    const linked = linkedMatch?.record || null;
    const saleDate = lift.dataVenda;
    const inRequestedStore = !hasStoreScope || sameStore(storeId, options.storeId);
    const inRequestedConsultant = !hasConsultantScope
      || sameStore(sellerId, options.consultantId);
    if (!inRequestedStore && !(options.consultantAcrossStores && hasConsultantScope && inRequestedConsultant)) continue;
    if (!inRequestedConsultant) continue;

    const isCrossStore = Boolean(options.consultantAcrossStores && hasConsultantScope
      && consultantHomeStore !== null && consultantHomeStore !== undefined
      && !sameStore(storeId, consultantHomeStore));
    const isRedactedProjection = redactedLiftRecords.has(lift);
    if (isRedactedProjection && !(options.consultantAcrossStores && hasConsultantScope)) continue;
    if (!originInfo && !isRedactedProjection) unknownOriginCount += 1;
    if (isCrossStore || isRedactedProjection) {
      liftCommercialSales.push({
        recordType:"lift",
        value:Number(lift.valorTotal || 0),
        storeId,
        crossStore:true,
      });
      continue;
    }
    const ambiguousLink = Boolean(linkKey) && linkedMatches.length > 1;
    const clientRecord = linkedMatch?.sourceType === "client" ? linked : null;
    const linkedSourceStoreId = linkedMatch?.sourceType === "lead"
      ? linked?.lojaAtual ?? linkedMatch.storeId
      : linkedMatch?.storeId;
    const linkedName = linked?.nome || linked?.name;
    const sale = {
      id:`lift:${storeId}:${lift.liftPedidoId}`,
      source:"LIFT",
      origin:originInfo?.origin || UNKNOWN_ORIGIN,
      originReviewLabel:pendingAttribution ? "" : lift.classificacaoOrigem?.originReviewLabel || linked?.originReviewLabel || "",
      recordType:"lift",
      name:lift.cliente?.nome || linkedName || "Não identificado",
      phone:lift.cliente?.telefone || lift.cliente?.phone || linked?.telefone || linked?.phone || "",
      consultantId:Number(sellerId),
      storeId:Number(storeId),
      value:Number(lift.valorTotal || 0),
      saleDate,
      client:clientRecord || null,
      record:lift,
      linkedSourceType:linkedMatch?.sourceType || null,
      linkedSourceId:linked?.id == null ? null : String(linked.id),
      linkedSourceStoreId:linkedSourceStoreId == null ? null : Number(linkedSourceStoreId),
      linkedSourcePhone:linked?.telefone || linked?.phone || "",
      linkedSourceChannelStoreId:linkedMatch?.sourceType === "lead"
        ? Number(linked?.whatsappStoreId ?? linked?.lojaOrigem ?? linkedMatch.storeId) || undefined
        : undefined,
      unresolvedSourceLink:pendingAttribution || Boolean(linkKey && !linkedMatch),
      ...(pendingAttribution ? { originBindingPending:true, warning:"Vínculo de origem pendente de confirmação." } : {}),
      vinculo:linked ? { chaveCRM:String(linkKey) } : null,
      unidentified:!linked,
      ...(LIFT_ORIGINS[lift.classificacaoOrigem?.tipo]?.visitDirect ? { visitDirect:true } : {}),
      ...(ambiguousLink ? { warning:"Vínculo CRM ambíguo; não foi atribuído." } : {}),
    };
    if (!originInfo) sale.origin = UNKNOWN_ORIGIN;
    liftCommercialSales.push({ ...sale, _category:originInfo?.category });
  }

  const legacyCommercialSales = legacy.sales.filter(sale => saoPauloDay(sale.saleDate) < LIFT_CUTOFF);
  const visibleLift = liftCommercialSales;
  const commercialSales = [...legacyCommercialSales, ...visibleLift];
  const storeTotalsSales = commercialSales.filter(sale => !sale.crossStore);
  const overallSales = hasConsultantScope && options.consultantAcrossStores
    ? commercialSales
    : storeTotalsSales;
  const totalQtd = overallSales.length;
  const totalValue = overallSales.reduce((sum, sale) => sum + Number(sale.value || 0), 0);
  const categoryForSale = sale => sale._category || (sale.origin === "Lead" ? "lead"
      : sale.origin === "Prospecção" ? "prospeccao"
        : ["Indicação", "Indicacao", "indicacao"].includes(sale.origin) ? "indicacao" : "porta");
  legacy.indicacao = {
    qtd:0,
    value:0,
    arrived:(options.clients || []).filter(client => (
      ["Indicação", "Indicacao", "indicacao"].includes(client.origin)
      && (!hasStoreScope || sameStore(client.storeId || 1, options.storeId))
      && (!hasConsultantScope || sameStore(client.consultantId, options.consultantId))
      && (!options.inPeriod || options.inPeriod(client.createdAt, options.periodRange))
    )).length,
    conv:0,
  };
  for (const [key, category] of [["lead", "lead"], ["prospeccao", "prospeccao"], ["porta", "porta"], ["indicacao", "indicacao"]]) {
    const categorySales = storeTotalsSales.filter(sale => categoryForSale(sale) === category);
    legacy[key].qtd = categorySales.length;
    legacy[key].value = categorySales.reduce((sum, sale) => sum + Number(sale.value || 0), 0);
    legacy[key].conv = legacy[key].arrived
      ? Math.min(100, Math.round((legacy[key].qtd / legacy[key].arrived) * 100))
      : 0;
  }
  legacy.totalQtd = totalQtd;
  legacy.totalValue = totalValue;
  legacy.ticketMedio = totalQtd ? Math.round(totalValue / totalQtd) : 0;
  legacy.sales = commercialSales.map(({ _category, ...sale }) => sale);
  legacy.manualWithoutLift = manualWithoutLift;
  legacy.unknownOriginCount = unknownOriginCount;
  return legacy;
}

export function computeSalesLedger(options = {}) {
  const legacy = computeLegacySalesLedger(options);
  return addLiftSales(legacy, options);
}